import { PlainButton } from '@customerio/pluma-components/react';
import clsx from 'clsx';
import { useState, useMemo } from 'react';
import { useLocation } from '@/services/router/compat';
import { GradientRing, gradientHoverTarget } from '@/components/feedback/gradient-ring';
import { useAuth } from '@/services/auth';
import { isBuilderPlan } from '@/services/auth/plan-slugs';
import * as styles from './index.css';
import * as marqueeStyles from './marquee.css';
import { getMarqueeSuggestionRows } from './static-suggestions';

interface MarqueeSuggestionsProps {
  onSuggestionClick: (message: string, index: number) => void;
  shouldIncludeHumanSupport?: boolean;
}

/**
 * A marquee-style scrolling display of suggestion chips, arranged in multiple rows.
 * Each row scrolls in alternating directions. The animation pauses when hovering
 * over a suggestion chip so users can click it.
 *
 * When the user is on a recognized page, route-specific suggestions are mixed in
 * alongside generic suggestions to provide contextually relevant prompts.
 */
export function MarqueeSuggestions({ onSuggestionClick, shouldIncludeHumanSupport = false }: MarqueeSuggestionsProps) {
  const [isPaused, setIsPaused] = useState(false);
  const pathname = useLocation().pathname;
  const { account } = useAuth();
  const isBuilder = isBuilderPlan(account);
  const rows = useMemo(
    () => getMarqueeSuggestionRows({ shouldIncludeHumanSupport, pathname, isBuilder }),
    [shouldIncludeHumanSupport, pathname, isBuilder],
  );

  return (
    <div className={marqueeStyles.marqueeWrapper}>
      {rows.map((row, rowIndex) => (
        <div key={rowIndex} className={marqueeStyles.marqueeContainer}>
          <div
            className={`${marqueeStyles.marqueeRow} ${
              rowIndex % 2 === 0 ? marqueeStyles.marqueeRowScrollLeft : marqueeStyles.marqueeRowScrollRight
            } ${isPaused ? marqueeStyles.marqueeRowPaused : ''}`}
          >
            {/* Render items twice for seamless loop */}
            {[...row, ...row].map((suggestion, index) => (
              <GradientRing
                key={`${rowIndex}-${index}`}
                radius={4}
                onMouseEnter={() => setIsPaused(true)}
                onMouseLeave={() => setIsPaused(false)}
              >
                <PlainButton
                  className={clsx(styles.suggestionButton, gradientHoverTarget)}
                  onClick={() => onSuggestionClick(suggestion.message, suggestion.index)}
                >
                  {suggestion.label}
                </PlainButton>
              </GradientRing>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
